import { useCallback, useEffect, useRef, useState } from 'react';
import { isFirebaseConfigured } from '@/lib/firebase';
import { ensureAnonymousAuth, getCurrentUser } from '@/services/authService';
import { sendChatMessage, markMessagesRead } from '@/services/chatService';
import { startPresence } from '@/services/presenceService';
import {
  cancelSupportRequest,
  createSupportRequest,
  getSupportSettings,
  subscribeSupportRequest,
} from '@/services/supportService';
import type { AiHistoryItem, ChatMode, SupportRequest } from '@/types/support';

const STORAGE_KEY = 'ddc_support_request_id';

export function useSupportSession() {
  const [mode, setMode] = useState<ChatMode>('ai');
  const [request, setRequest] = useState<SupportRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const stopPresenceRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const requestUnsubRef = useRef<(() => void) | null>(null);

  const attachRequestSubscription = useCallback((requestId: string) => {
    requestUnsubRef.current?.();
    requestUnsubRef.current = subscribeSupportRequest(requestId, (r) => {
      if (!r) {
        localStorage.removeItem(STORAGE_KEY);
        setRequest(null);
        setMode('ai');
        return;
      }
      setRequest(r);
      if (r.status === 'waiting') setMode('waiting');
      else if (r.status === 'accepted') setMode('human');
      else if (r.status === 'closed' || r.status === 'cancelled' || r.status === 'rejected') {
        setMode('ended');
        localStorage.removeItem(STORAGE_KEY);
      }
    });
  }, []);

  // Restore active session
  useEffect(() => {
    if (!isFirebaseConfigured()) return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    let cancelled = false;

    void ensureAnonymousAuth().then(() => {
      if (cancelled) return;
      attachRequestSubscription(saved);
    });

    return () => {
      cancelled = true;
      requestUnsubRef.current?.();
      requestUnsubRef.current = null;
    };
  }, [attachRequestSubscription]);

  useEffect(() => {
    if (!request || (mode !== 'waiting' && mode !== 'human')) {
      stopPresenceRef.current?.();
      stopPresenceRef.current = null;
      return;
    }
    const uid = getCurrentUser()?.uid ?? request.customerId;
    let cancelled = false;
    void startPresence({
      uid,
      role: 'customer',
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      requestId: request.requestId,
    }).then((stop) => {
      if (cancelled) stop();
      else stopPresenceRef.current = stop;
    });
    return () => {
      cancelled = true;
      stopPresenceRef.current?.();
      stopPresenceRef.current = null;
    };
  }, [request?.requestId, mode]);

  // Auto-timeout waiting requests
  useEffect(() => {
    if (mode !== 'waiting' || !request) return;
    let timer: number | undefined;
    void getSupportSettings().then((settings) => {
      const remaining = settings.requestTimeoutMs - (Date.now() - request.createdAt);
      if (remaining <= 0) {
        void cancelSupportRequest(request.requestId, request.customerId);
        return;
      }
      timer = window.setTimeout(() => {
        void cancelSupportRequest(request.requestId, request.customerId);
      }, remaining);
    });
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [mode, request?.requestId, request?.createdAt, request?.customerId]);

  const connectToHuman = useCallback(async (aiHistory: AiHistoryItem[], customerName?: string | null) => {
    if (!isFirebaseConfigured()) {
      setError('Human support is not configured yet.');
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      const { request: created } = await createSupportRequest({
        aiHistory,
        customerName,
      });
      localStorage.setItem(STORAGE_KEY, created.requestId);
      setRequest(created);
      setMode('waiting');
      attachRequestSubscription(created.requestId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect to support');
    } finally {
      setConnecting(false);
    }
  }, [attachRequestSubscription]);

  const cancelWaiting = useCallback(async () => {
    if (!request) return;
    try {
      await cancelSupportRequest(request.requestId, request.customerId);
      localStorage.removeItem(STORAGE_KEY);
      setMode('ended');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel request');
    }
  }, [request]);

  const sendHumanMessage = useCallback(
    async (text: string) => {
      if (!request || mode !== 'human') return;
      const uid = getCurrentUser()?.uid ?? request.customerId;
      await sendChatMessage({
        requestId: request.requestId,
        senderId: uid,
        senderRole: 'customer',
        text,
      });
      await markMessagesRead(request.requestId, 'customer');
    },
    [request, mode],
  );

  const resetToAi = useCallback(() => {
    requestUnsubRef.current?.();
    requestUnsubRef.current = null;
    localStorage.removeItem(STORAGE_KEY);
    setRequest(null);
    setMode('ai');
    setError(null);
  }, []);

  return {
    mode,
    request,
    error,
    connecting,
    online,
    connectToHuman,
    cancelWaiting,
    sendHumanMessage,
    resetToAi,
    setMode,
  };
}
